/**
 * Dashboard share links. A dashboard is a browser tab, not a route, so the
 * address bar never reflected the open view. useDashboardUrlSync mirrors the
 * current view into readable query params as you navigate and filter, so
 * copying the address bar is the shareable link; pasting it replays the same
 * surface + dates + filters. e.g.
 *   /?dashboard=analytics&start=2026-08-14&end=2026-09-12&attribution=blended
 *
 * The param vocabulary is a small closed set (DASHBOARD_PARAM_KEYS) so the
 * writer can fully rewrite it on every change — no stale key from a previous
 * dashboard can leak into the next view. Tenant isolation stays server-side:
 * the backend scopes every read to the recipient's own session, so a
 * non-teammate only ever sees their own company's data.
 */
import LocalStorageKey from '@enums/LocalStorageKey';
import LocalStorageService from '@services/LocalStorageService';

/**
 * The shareable dashboards. For each surface: the app-store control namespace
 * it reads through `useDashboardControl(namespace, …)` (`null` = tenant-only
 * dashboard with no view controls to carry), and the human-readable `dashboard=`
 * URL slug. Keys match `DASHBOARD_TABS`.
 */
const SURFACES = {
  // The controls keep their stored namespace from when the page was the Performance tab.
  analytics:        { namespace: 'performance',      slug: 'analytics' },
  creatives:        { namespace: 'creative',         slug: 'creatives' },
  leadAttribution:  { namespace: 'leadAttribution',  slug: 'lead-attribution' },
  campaignTimeline: { namespace: 'campaignTimeline', slug: 'campaign-timeline' },
  gallery:          { namespace: null,               slug: 'gallery' },
  monitors:         { namespace: null,               slug: 'monitors' },
} as const;

export type ShareableSurface = keyof typeof SURFACES;

/** surface → control namespace (`null` = tenant-only). */
export const SHARE_NAMESPACE = Object.fromEntries(
  (Object.keys(SURFACES) as ShareableSurface[]).map((s) => [s, SURFACES[s].namespace]),
) as Record<ShareableSurface, string | null>;

const SLUG_SURFACE: Record<string, ShareableSurface> = {
  ...Object.fromEntries(
    (Object.keys(SURFACES) as ShareableSurface[]).map((s) => [SURFACES[s].slug, s]),
  ),
  performance: 'analytics', // a link shared before the Performance tab became the Analytics dashboard
};

/** The dashboard-identifier param. */
export const DASHBOARD_PARAM = 'dashboard';

/**
 * One spec per view control, keyed by the control key each dashboard stores it
 * under (`useDashboardControl(ns, <key>)`). `read` pulls the control's value out
 * of query params; `write` emits the params for a value (omitting defaults, to
 * keep the URL clean). Control keys are consistent across dashboards, so a
 * single table covers every surface.
 */
interface ControlSpec {
  key: string;
  params: string[];
  read: (p: URLSearchParams) => unknown;
  write: (value: unknown, set: (param: string, value: string) => void) => void;
}

/** A plain string control stored under `key`, carried in a single `param`. */
const str = (key: string, param = key): ControlSpec => ({
  key,
  params: [param],
  read: (p) => p.get(param) ?? undefined,
  write: (value, set) => {
    if (value) set(param, String(value));
  },
});

const CONTROLS: ControlSpec[] = [
  {
    key: 'dates',
    params: ['start', 'end'],
    read: (p) => {
      const start = p.get('start');
      const end = p.get('end');
      return start && end ? [start, end] : undefined;
    },
    write: (value, set) => {
      if (Array.isArray(value) && value[0] && value[1]) {
        set('start', String(value[0]));
        set('end', String(value[1]));
      }
    },
  },
  str('attribution'),
  str('scope'),
  str('accountRef', 'account'),
  str('metric'),
];

/** Every param this feature owns — the writer clears all of these, then sets the active ones. */
export const DASHBOARD_PARAM_KEYS: string[] = [
  DASHBOARD_PARAM,
  ...CONTROLS.flatMap((c) => c.params),
];

/** The params for a view: `{ dashboard, …controls }`. Defaults are omitted. */
export function encodeDashboardParams(
  surface: ShareableSurface,
  controls: Record<string, unknown>,
): Record<string, string> {
  const out: Record<string, string> = { [DASHBOARD_PARAM]: SURFACES[surface].slug };
  for (const spec of CONTROLS) {
    if (spec.key in controls) {
      spec.write(controls[spec.key], (param, value) => {
        out[param] = value;
      });
    }
  }
  return out;
}

/** Read a view out of query params, or null if there's no (valid) dashboard param. */
export function decodeDashboardParams(
  params: URLSearchParams,
): { surface: ShareableSurface; controls: Record<string, unknown> } | null {
  const slug = params.get(DASHBOARD_PARAM) ?? '';
  // Own keys only: `?dashboard=constructor` is not a dashboard.
  const surface = Object.prototype.hasOwnProperty.call(SLUG_SURFACE, slug) ? SLUG_SURFACE[slug] : undefined;
  if (!surface) return null;
  const controls: Record<string, unknown> = {};
  for (const spec of CONTROLS) {
    const value = spec.read(params);
    if (value !== undefined) controls[spec.key] = value;
  }
  return { surface, controls };
}

/**
 * Capture the dashboard params the moment the app mounts, before the auth gate
 * can redirect an unauthenticated teammate to login (which drops the URL). The
 * sync hook reads URL-or-stash and clears the stash once consumed. No-op when
 * there's no dashboard param.
 */
export function stashPendingDashboardParams(): void {
  const sp = new URLSearchParams(window.location.search);
  if (!sp.get(DASHBOARD_PARAM)) return;
  const keep = new URLSearchParams();
  for (const key of DASHBOARD_PARAM_KEYS) {
    const value = sp.get(key);
    if (value != null) keep.set(key, value);
  }
  LocalStorageService.setItem(LocalStorageKey.PENDING_DASHBOARD_SHARE, keep.toString());
}

export function readPendingDashboardParams(): URLSearchParams | null {
  const stashed = LocalStorageService.getItem<string>(
    LocalStorageKey.PENDING_DASHBOARD_SHARE,
  );
  return stashed ? new URLSearchParams(stashed) : null;
}

export function clearPendingDashboardParams(): void {
  LocalStorageService.removeItem(LocalStorageKey.PENDING_DASHBOARD_SHARE);
}

/** Stable, order-independent signature of just the dashboard params — for change detection. */
export function dashboardParamSignature(params: URLSearchParams): string {
  return DASHBOARD_PARAM_KEYS.map((k) => `${k}=${params.get(k) ?? ''}`).join('&');
}
